import {StateLabel} from '@primer/react'
import {graphql, useFragment} from 'react-relay'

import type {HeaderState$key} from './__generated__/HeaderState.graphql'
import {TEST_IDS} from '../../constants/test-ids'
import {useFeatureFlags} from '@github-ui/react-core/use-feature-flag'

type HeaderStateProps = {
  isSticky: boolean // whether it is rendered in the normal header or in the sticky header
  stateData: HeaderState$key
}

export function HeaderState({isSticky, stateData}: HeaderStateProps) {
  const {state, stateReason} = useFragment(
    graphql`
      fragment HeaderState on Issue {
        state
        stateReason(enableDuplicate: true)
      }
    `,
    stateData,
  )

  const {issues_react_close_as_duplicate} = useFeatureFlags()
  const reason = !issues_react_close_as_duplicate && stateReason === 'DUPLICATE' ? 'NOT_PLANNED' : stateReason

  return (
    <div>
      <StateLabel
        data-testid={TEST_IDS.headerState}
        status={issueStateStatus(state, stateReason ?? '')}
        sx={{whiteSpace: 'nowrap'}}
      >
        {issueStateString(`${state}${isSticky ? ' sticky' : ''}`, reason ?? '')}
      </StateLabel>
    </div>
  )
}

const issueStateStatus = (
  state: string,
  stateReason: string,
): 'issueOpened' | 'issueClosed' | 'issueClosedNotPlanned' => {
  switch (state) {
    case 'OPEN':
      return 'issueOpened'
    case 'CLOSED':
      return stateReason === 'NOT_PLANNED' || stateReason === 'DUPLICATE' ? 'issueClosedNotPlanned' : 'issueClosed'
    default:
      return 'issueClosed'
  }
}

const issueStateString = (state: string, stateReason: string): string => {
  switch (state) {
    case 'OPEN':
    case 'OPEN sticky':
      return 'Open'
    case 'CLOSED':
      // eslint-disable-next-line no-case-declarations
      const base = 'Closed'
      return stateReason === 'NOT_PLANNED'
        ? `${base} as not planned`
        : stateReason === 'DUPLICATE'
          ? `${base} as duplicate`
          : base
    case 'CLOSED sticky':
      return stateReason === 'NOT_PLANNED' ? `Not planned` : stateReason === 'DUPLICATE' ? `Duplicate` : 'Closed'
    default:
      return ''
  }
}

try{ HeaderState.displayName ||= 'HeaderState' } catch {}